﻿import { useParams, useNavigate } from "react-router-dom";
import { Settings, History, RotateCcw } from "lucide-react";
import ReactJson from "@microlink/react-json-view";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomButton from "@/components/desktop/button/CustomButton";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";

import { dateToLocale } from "@/services/Utils";
import useListController from "@/hooks/list/useListController";
import { useIntegrationPurchases, usePendingPurchase } from "@/hooks/integrations/useIntegration";

import { base_table_purchase } from "../integrations.constants";

const IntegrationPurchase = () => {
	const navigate = useNavigate();
	const { option, element_id } = useParams();

	const isOpen = option === "compras";

	const { tableState, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: [],
		baseTable: base_table_purchase,
	});

	const { data, isLoading: isLoadingList } = useIntegrationPurchases(
		{
			...tableState,
		},
		isOpen,
	);

	const { mutate: pendingIntegration, isLoading: isLoadingPending } = usePendingPurchase();

	const onClose = () => {
		navigate("/configuracion/integraciones");
	};

	const handlePending = (purchase_queue_id) => {
		const payload = {
			filters: { ...tableState },
			id: purchase_queue_id,
		};

		pendingIntegration(payload, {
			onSuccess: () => {
				toast.success("Se ha marcado la compra como pendiente");
			},
			onError: (error) => {
				const errorMsg = error?.response?.data?.message || error?.message || "Error desconocido, contacte al administrador";
				toast.error(errorMsg);
			},
		});
	};

	if (!isOpen) return null;

	const columns = [
		{
			Header: "Fecha",
			accessor: "created_at",
			width: 100,
			Cell: ({ value }) => {
				return <span>{dateToLocale(value)}</span>;
			},
		},
		{
			Header: "Datos",
			accessor: "input_data",
			disableSortBy: true,
			width: 300,
			Cell: ({ value }) => {
				return <ReactJson src={value} collapsed={true} />;
			},
		},
		{
			Header: "Respuesta",
			accessor: "response_message",
			disableSortBy: true,
			width: 200,
			Cell: ({ value }) => {
				const displayValue = typeof value === "object" && value !== null ? JSON.stringify(value, null, 2) : String(value || "");
				return <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{displayValue}</div>;
			},
		},
		{
			Header: "Completado",
			accessor: "completed",
			disableSortBy: true,
			width: 100,
			Cell: ({ value }) => {
				return (
					<CustomBadge variant={value ? "success" : "danger"} size="md">
						{value ? "Completada" : "Pendiente"}
					</CustomBadge>
				);
			},
		},
		{
			Header: "",
			disableSortBy: true,
			accessor: "id",
			width: 50,
			Cell: ({ value }) => {
				return (
					<div style={{ textAlign: "center" }}>
						<CustomDropdown
							title="Acciones"
							items={[
								{
									label: "Marcar como Pendiente",
									action: () => handlePending(value),
									icon: RotateCcw,
								},
							]}
							align="right"
						/>
					</div>
				);
			},
		},
	];

	const isLoading = isLoadingList || isLoadingPending;

	return (
		<CustomModalForm
			open={isOpen}
			onClose={onClose}
			title={"Compras Sincronizadas"}
			size="xl"
			onSubmit={null}
			isLoading={isLoading}
			gridTable={false}
			showSave={false}
			footerLeft={
				<>
					<CustomButton
						variant="secondary"
						disabled={isLoading}
						startIcon={<Settings size={16} />}
						onClick={() => navigate("/configuracion/integraciones/editar/" + element_id)}
					>
						Ver Configuración
					</CustomButton>
					<CustomButton
						variant="secondary"
						disabled={isLoading}
						startIcon={<History size={16} />}
						onClick={() => navigate("/configuracion/integraciones/aplicaciones/" + element_id)}
					>
						Ver Aplicaciones
					</CustomButton>
				</>
			}
		>
			<ListScaffold
				data={data?.data || []}
				totalItems={data?.total || 0}
				totalPages={data?.last_page || 0}
				toItem={data?.to || 0}
				fromItem={data?.from || 0}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				customHeight={"56vh"}
				customStyle={{ padding: 0 }}
				isLoading={isLoading}
			/>
		</CustomModalForm>
	);
};

export default IntegrationPurchase;
